import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Search } from 'lucide-react'
import { fetchCustomers } from '@/api/customers'
import { fetchThreatDownEndpoints, fetchThreatDownSummary } from '@/api/threatdown'
import { fetchThreatDownSyncStatus } from '@/api/sync'
import { CustomerFilter } from '@/components/CustomerFilter'
import { SimplePieChart } from '@/components/charts/SimplePieChart'
import { PostureLink, TrafficLightDot } from '@/components/PostureIndicator'
import { ThreatDownNav } from '@/components/threatdown/ThreatDownNav'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/auth/AuthContext'
import { formatDateTime } from '@/lib/formatAgent'
import { TRAFFIC } from '@/lib/trafficLight'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 50

const STATUS_CHART = [
  { status: 'green', label: TRAFFIC.green.label, colour: '#10b981' },
  { status: 'amber', label: TRAFFIC.amber.label, colour: '#f59e0b' },
  { status: 'red', label: TRAFFIC.red.label, colour: '#ef4444' },
  { status: 'grey', label: TRAFFIC.grey.label, colour: '#94a3b8' },
]

const LABEL_TO_STATUS = Object.fromEntries(STATUS_CHART.map((row) => [row.label, row.status]))
const STATUS_TO_LABEL = Object.fromEntries(STATUS_CHART.map((row) => [row.status, row.label]))
const STATUS_COLOUR = Object.fromEntries(STATUS_CHART.map((row) => [row.label, row.colour]))

function countsToChartData(counts) {
  if (!counts) return []
  return STATUS_CHART.map((row) => ({
    label: row.label,
    value: counts[row.status] ?? 0,
  })).filter((row) => row.value > 0)
}

function SectionPieCard({ title, counts, section, selectedFilter, onSelectFilter }) {
  const chartData = useMemo(() => countsToChartData(counts), [counts])
  const activeLabel =
    selectedFilter?.section === section ? STATUS_TO_LABEL[selectedFilter.status] : null

  function handleChartClick(label) {
    const status = LABEL_TO_STATUS[label]
    if (!status) return
    onSelectFilter?.({ section, status })
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">{title}</CardTitle>
          {selectedFilter?.section === section ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onSelectFilter?.(null)}
            >
              Clear filter
            </Button>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">
          Click a slice or legend item to filter the endpoint list.
        </p>
      </CardHeader>
      <CardContent>
        <SimplePieChart
          data={chartData}
          activeLabel={activeLabel}
          onItemClick={handleChartClick}
          colourFor={(entry) => STATUS_COLOUR[entry.label]}
          emptyMessage={`No ${title.toLowerCase()} data yet`}
          heightClassName="h-[180px]"
        />
      </CardContent>
    </Card>
  )
}

function AteraLinkStrip({ counts, selectedFilter, onSelectFilter }) {
  const linked = counts?.green ?? 0
  const unlinked = counts?.amber ?? 0
  const unknown = counts?.grey ?? 0
  const action = counts?.red ?? 0

  function isSelected(status) {
    return selectedFilter?.section === 'atera-link' && selectedFilter?.status === status
  }

  function select(status) {
    onSelectFilter?.({ section: 'atera-link', status })
  }

  const chips = [
    { status: 'green', label: 'Linked', value: linked },
    { status: 'amber', label: 'Not linked', value: unlinked },
    { status: 'red', label: 'Action needed', value: action },
    { status: 'grey', label: 'Unknown', value: unknown },
  ].filter((chip) => chip.value > 0)

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">Atera link</p>
          <p className="text-xs text-muted-foreground">
            Whether each ThreatDown endpoint maps to an Atera device in Dash.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          {chips.map((chip) => {
            const selected = isSelected(chip.status)
            return (
              <button
                key={chip.status}
                type="button"
                onClick={() => select(chip.status)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs transition-colors hover:bg-muted/40',
                  selected && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
                )}
              >
                <TrafficLightDot status={chip.status} className="h-2 w-2" />
                <span className="font-medium tabular-nums">{chip.value}</span>
                <span className="text-muted-foreground">{chip.label}</span>
              </button>
            )
          })}
          {selectedFilter?.section === 'atera-link' ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => onSelectFilter?.(null)}
            >
              Clear
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}

function OnlineBadge({ online }) {
  if (online === true) return <Badge variant="success">Online</Badge>
  if (online === false) return <Badge variant="secondary">Offline</Badge>
  return <span className="text-muted-foreground">—</span>
}

function ProtectionBadge({ status }) {
  if (!status) return <span className="text-muted-foreground">—</span>
  const text = String(status).toLowerCase()
  if (['active', 'protected'].includes(text)) {
    return <Badge variant="success">{status}</Badge>
  }
  if (['inactive', 'off', 'disabled', 'failed'].some((x) => text.includes(x))) {
    return <Badge variant="destructive">{status}</Badge>
  }
  return <Badge variant="warning">{status}</Badge>
}

function groupKeyForEndpoint(row, groupByCustomer) {
  if (groupByCustomer) {
    return row.customer_name?.trim() || 'Unlinked / unknown customer'
  }
  return row.account_name?.trim() || 'Unknown site'
}

function groupEndpoints(items, groupByCustomer) {
  const groups = new Map()
  for (const row of items) {
    const key = groupKeyForEndpoint(row, groupByCustomer)
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(row)
  }
  return Array.from(groups.entries())
    .map(([name, endpoints]) => ({
      name,
      endpoints: endpoints.slice().sort((a, b) =>
        (a.hostname || '').localeCompare(b.hostname || '', undefined, { sensitivity: 'base' }),
      ),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
}

function groupSummary(endpoints) {
  const review = endpoints.filter((row) => row.overall_status === 'amber').length
  const action = endpoints.filter((row) => row.overall_status === 'red').length
  const offline = endpoints.filter((row) => row.online === false).length
  const bits = [`${endpoints.length} endpoint${endpoints.length === 1 ? '' : 's'}`]
  if (action > 0) bits.push(`${action} action needed`)
  if (review > 0) bits.push(`${review} review`)
  if (offline > 0) bits.push(`${offline} offline`)
  return bits.join(' · ')
}

function EndpointRows({ items, showCustomer }) {
  return items.map((row) => {
    const traffic = TRAFFIC[row.overall_status] ?? TRAFFIC.grey
    const issueTitle = Array.isArray(row.issues) && row.issues.length ? row.issues.join('; ') : undefined
    return (
      <tr key={row.id} className="border-b border-border/60 last:border-0">
        <td className="px-4 py-3">
          <Link
            to={`/threatdown/endpoints/${row.id}`}
            className="font-medium text-primary hover:underline"
          >
            {row.hostname}
          </Link>
          {row.display_name && row.display_name !== row.hostname && (
            <p className="text-xs text-muted-foreground">{row.display_name}</p>
          )}
          {row.agent_id ? (
            <p className="text-xs text-muted-foreground">
              <Link
                to={`/agents/${row.agent_id}?tab=security`}
                className="text-primary hover:underline"
              >
                Atera device
              </Link>
            </p>
          ) : (
            <p className="text-xs text-amber-600">Not linked to Atera</p>
          )}
        </td>
        {showCustomer && (
          <td className="px-4 py-3 text-muted-foreground">{row.customer_name || '—'}</td>
        )}
        <td className="px-4 py-3">{row.account_name || '—'}</td>
        <td className="px-4 py-3">
          <ProtectionBadge status={row.protection_status} />
        </td>
        <td className="px-4 py-3">
          <OnlineBadge online={row.online} />
        </td>
        <td className="px-4 py-3">{row.engine_version || '—'}</td>
        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
          {formatDateTime(row.last_seen_at)}
        </td>
        <td className="px-4 py-3" title={issueTitle}>
          {row.agent_id ? (
            <PostureLink
              agentId={row.agent_id}
              status={row.overall_status}
              tab="security"
              label="ThreatDown"
            />
          ) : (
            <span className="inline-flex items-center gap-2">
              <TrafficLightDot status={row.overall_status} className="h-2.5 w-2.5" />
              <Badge variant={traffic.badge}>{traffic.label}</Badge>
            </span>
          )}
        </td>
      </tr>
    )
  })
}

function EndpointGroupPanel({ group, showCustomerColumn, groupByCustomer }) {
  const [open, setOpen] = useState(false)
  const hideCustomerInTable = groupByCustomer

  return (
    <CollapsePanel
      id={`threatdown-group-${group.name}`}
      title={group.name}
      collapsedSummary={groupSummary(group.endpoints)}
      open={open}
      onOpenChange={setOpen}
      bodyClassName="p-0"
    >
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
              <th className="px-4 py-3 font-medium">Endpoint</th>
              {showCustomerColumn && !hideCustomerInTable && (
                <th className="px-4 py-3 font-medium">Customer</th>
              )}
              <th className="px-4 py-3 font-medium">Site</th>
              <th className="px-4 py-3 font-medium">Protection</th>
              <th className="px-4 py-3 font-medium">Online</th>
              <th className="px-4 py-3 font-medium">Engine</th>
              <th className="px-4 py-3 font-medium">Last seen</th>
              <th className="px-4 py-3 font-medium">Overall</th>
            </tr>
          </thead>
          <tbody>
            <EndpointRows
              items={group.endpoints}
              showCustomer={showCustomerColumn && !hideCustomerInTable}
            />
          </tbody>
        </table>
      </div>
    </CollapsePanel>
  )
}

export function ThreatDown() {
  const { isCustomer, canFilterCustomers } = useAuth()
  const [customers, setCustomers] = useState([])
  const [customerIds, setCustomerIds] = useState([])
  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [syncStatus, setSyncStatus] = useState(null)
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pages, setPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [postureFilter, setPostureFilter] = useState(null)

  useEffect(() => {
    if (canFilterCustomers) {
      fetchCustomers().then(setCustomers).catch(() => {})
    }
    fetchThreatDownSyncStatus().then(setSyncStatus).catch(() => {})
  }, [canFilterCustomers])

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 350)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    setSummaryLoading(true)
    setPostureFilter(null)
    fetchThreatDownSummary(customerIds)
      .then(setSummary)
      .catch(() => setSummary(null))
      .finally(() => setSummaryLoading(false))
  }, [customerIds])

  const loadEndpoints = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        search: search || undefined,
        sort_by: 'hostname',
        sort_dir: 'asc',
      }
      if (postureFilter?.section && postureFilter?.status) {
        params.section = postureFilter.section
        params.status = postureFilter.status
      }
      const data = await fetchThreatDownEndpoints(params, customerIds)
      setItems(data.items)
      setTotal(data.total)
      setPages(data.pages)
    } catch {
      setError('Could not load ThreatDown endpoints.')
    } finally {
      setLoading(false)
    }
  }, [page, search, customerIds, postureFilter])

  useEffect(() => {
    loadEndpoints()
  }, [loadEndpoints])

  function handlePostureFilter(next) {
    if (!next) {
      setPostureFilter(null)
      setPage(1)
      return
    }
    setPostureFilter((current) =>
      current?.section === next.section && current?.status === next.status ? null : next,
    )
    setPage(1)
  }

  const lastSync = syncStatus?.last_sync
  const groupByCustomer = canFilterCustomers
  const endpointGroups = groupEndpoints(items, groupByCustomer)

  return (
    <div className="space-y-6">
      <ThreatDownNav />

      <Card>
        <CardContent className="pt-6">
          <div
            className={cn(
              'flex flex-col gap-4',
              canFilterCustomers && 'md:flex-row md:items-end md:justify-between',
            )}
          >
            {canFilterCustomers && (
              <CustomerFilter
                id="threatdown-customer"
                label="Company"
                value={customerIds}
                onChange={setCustomerIds}
                customers={customers}
                className="md:min-w-[280px] md:max-w-sm"
              />
            )}
            <div className={cn('space-y-1', canFilterCustomers && 'md:text-right md:shrink-0')}>
              <div
                className={cn(
                  'flex flex-wrap items-center gap-2',
                  canFilterCustomers && 'md:justify-end',
                )}
              >
                <p className="text-sm font-medium">Sync status</p>
                {syncStatus?.running && <Badge variant="warning">Sync running</Badge>}
              </div>
              <p className="text-sm text-muted-foreground">
                {lastSync ? (
                  <>
                    Last synced {formatDateTime(lastSync.completed_at || lastSync.started_at)}
                    {lastSync.endpoints_synced != null
                      ? ` · ${lastSync.endpoints_synced} endpoints`
                      : ''}
                    {lastSync.status === 'failed' ? ' · last run failed' : ''}
                  </>
                ) : (
                  'No ThreatDown sync recorded yet. Configure credentials and run a sync from Admin.'
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {summaryLoading ? (
        <div className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <Skeleton className="h-56 w-full" />
            <Skeleton className="h-56 w-full" />
          </div>
          <Skeleton className="h-16 w-full" />
        </div>
      ) : summary?.total_endpoints > 0 ? (
        <div className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-2">
            <SectionPieCard
              title="Protection"
              counts={summary.protection}
              section="protection"
              selectedFilter={postureFilter}
              onSelectFilter={handlePostureFilter}
            />
            <SectionPieCard
              title="Connectivity"
              counts={summary.connectivity}
              section="connectivity"
              selectedFilter={postureFilter}
              onSelectFilter={handlePostureFilter}
            />
          </div>
          <AteraLinkStrip
            counts={summary.atera_link}
            selectedFilter={postureFilter}
            onSelectFilter={handlePostureFilter}
          />
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            No ThreatDown endpoints in this scope. Run a ThreatDown sync from Admin after configuring
            credentials.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            Endpoints {total > 0 && <span className="text-muted-foreground">({total})</span>}
          </CardTitle>
          {postureFilter && (
            <p className="text-xs text-muted-foreground">
              Filtered by{' '}
              {postureFilter.section === 'atera-link'
                ? 'Atera link'
                : postureFilter.section}{' '}
              — {STATUS_TO_LABEL[postureFilter.status] || postureFilter.status}.{' '}
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => handlePostureFilter(null)}
              >
                Clear filter
              </button>
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 max-w-md">
            <Label htmlFor="threatdown-search">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="threatdown-search"
                placeholder="Hostname, display name, site…"
                className="pl-9"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : !items.length ? (
            <p className="text-sm text-muted-foreground">No endpoints match your filters.</p>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Grouped by {groupByCustomer ? 'customer' : 'site'}. Overall Review means attention
                needed — non-active protection, not linked to Atera, or offline for over 4 weeks
                (hover Overall for the reason).
              </p>
              {endpointGroups.map((group) => (
                <EndpointGroupPanel
                  key={group.name}
                  group={group}
                  showCustomerColumn={canFilterCustomers}
                  groupByCustomer={groupByCustomer}
                />
              ))}
            </div>
          )}

          {pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {pages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
